import { useContext, useEffect, useState } from "react";
import { Rating } from "@mui/material";

import { convertDateToUKFormat } from "../../../../utils";
import { openDisputeChatView, fetchDispute } from "../service";
import DivSpinner from "../../../../components/divSpinner";
import useCustomNavigate from "../../../../components/hooks/useCustomNavigate";
import Context from "../context";

const SelectedDispute = ({ selectedDispute }) => {
    const context = useContext(Context);
    const { navigateToCustom } = useCustomNavigate();
    const [dispute, setDispute] = useState({
        loading: false,
        error: null,
        data: null,
    });

    const [isLoadingChat, setIsLoadingChat] = useState(false);

    useEffect(() => {
        const fetchDetails = async () => {
            setDispute({ loading: true, error: null, data: null });
            try {
                const response = await fetchDispute({ doc_id: selectedDispute.id });
                setDispute({ loading: false, error: null, data: response.dispute });
            } catch (error) {
                setDispute({ loading: false, error: error.message, data: null });
            }
        };

        fetchDetails();
    }, [selectedDispute.id]);

    const handleClose = () => {
        context.dispatch({
            type: 'SET_SELECTED_DISPUTE',
            selectedDispute: null,
        });
    };

    const handleOpenChat = async () => {
        setIsLoadingChat(true);
        await openDisputeChatView(dispute.data.dispute_job_id, context.dispatch);
        setIsLoadingChat(false);
    }

    const { loading, data } = dispute;
    const ftm = data?.ftm;
    const job = data?.job;
    const disputesImages = data?.disputes_images;
    const subSkill = job?.sub_skill;

    return (
        <div className="card position-sticky top-10 mb-4 p-4">
            {loading ? (
                <div style={{ height: `${window.innerHeight - 350}px` }}>
                    <DivSpinner />
                </div>
            ) : (
                data && (
                    <>
                        {/* Dispute Header */}
                        <div className="d-flex justify-content-between align-items-start">
                            <div>
                                <h5 className="mb-1">Dispute #{data.dispute_job_id}</h5>
                                <small className="text-muted">
                                    Created: {convertDateToUKFormat(data.dispute_createAt)}
                                </small>
                            </div>
                            <button className="btn-close" aria-label="Close" onClick={handleClose}></button>
                        </div>

                        {/* Dispute Text */}
                        <div className="card card-plain p-0 mt-3">
                            <div className="card-body p-0">
                                <p className="text-sm">{data.dispute_text}</p>
                            </div>
                        </div>

                        {/* FTM Info */}
                        <div className="card card-plain mt-2">
                            <div className="card-body p-3 pt-0">
                                <div className="row">
                                    <div className="col-4 ps-0">
                                        <img
                                            src={ftm?.ftm_profile_image_path || "/assets/img/user-removebg-preview.png"}
                                            alt="ftm_profile_image"
                                            className="border-radius-lg"
                                            style={{ width: "80px", height: "80px" }}
                                        />
                                    </div>
                                    <div className="col-8 my-auto pe-0 text-end">
                                        <h5 className="mb-0 font-weight-bolder mt-2">
                                            {ftm?.ftm_forename} {ftm?.ftm_surname}
                                        </h5>
                                        <Rating
                                            name="ftm-rating"
                                            value={Number(ftm?.ftm_customer_ratings) || 0}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Job Information */}
                        <div className="card card-plain p-0 mb-3">
                            <div className="card-header pb-0 px-0">
                                <h6 className="mb-0">Job Information</h6>
                            </div>
                            <div className="card-body p-0">
                                <p className="text-sm">{job?.job_description}</p>
                                <ul className="list-group">
                                    <li className="list-group-item border-0 ps-0 py-1 text-sm">
                                        <strong className="text-dark">Skill:</strong> &nbsp; {subSkill?.sub_skill_name}
                                    </li>
                                    <li className="list-group-item border-0 ps-0 py-1 text-sm">
                                        <strong className="text-dark">Sub skill:</strong> &nbsp; {subSkill?.skill?.skill_name}
                                    </li>
                                    <li className="list-group-item border-0 ps-0 py-1 text-sm">
                                        <strong className="text-dark">Address:</strong> &nbsp; {job?.job_address}
                                    </li>
                                    <li className="list-group-item border-0 ps-0 py-1 text-sm">
                                        <strong className="text-dark">Created At:</strong> &nbsp; {convertDateToUKFormat(job?.job_createdAt)}
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {disputesImages?.length > 0 ? (
                            <div className="scroll-container mt-2 mb-3">
                                {disputesImages?.map((image, index) => (
                                    <img src={image.disputes_image_path} alt="disputes_images" key={index} className="mb-1" />
                                ))}
                            </div>
                        ) : (
                            <div className="scroll-container mt-2 mb-3">
                                <p className="text-center" style={{paddingTop: '10px'}}>No images found</p>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="d-flex justify-content-end">
                            <button 
                                className="btn btn-outline-secondary me-2 btn-sm" 
                                onClick={handleOpenChat}
                                disabled={isLoadingChat}
                            >
                                {isLoadingChat ? 'Loading Chat...' : 'Chat'}
                            </button>
                            <button 
                                className="btn btn-success btn-sm" 
                                onClick={() => navigateToCustom(`/jobs/view/${data?.dispute_job_id}`)}
                                disabled={isLoadingChat}
                            >
                                {isLoadingChat ? 'Loading Chat...' : 'Job details'}
                            </button>
                        </div>
                    </>
                )
            )}
        </div>
    );
};

export default SelectedDispute;